import axios from "axios";
import { DateTime } from "luxon";
import process from "prop-types/prop-types";
import React, { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import useSWR from "swr";

import Breadcrumbs from "../../component/breadcrumbs";
import CustomHelmet from "../../component/CustomHelmet";
import FullScreenLoader from "../../component/FullScreenLoader";
import Intro from "../../component/Intro";
import BaseContainer from "../../component/layout/base-container";
import PrintQRCodes from "../../component/PrintQRCodes";
import customStyles from "../../customStyles/tanList.module.scss";
import { getToken } from "../../lib/authentication";
import { auth_fetcher } from "../../lib/fetcher";

export default function TanList({ user }) {
  const navigate = useNavigate();

  const params = useParams();
  const surveyID = params.surveyId;
  const { data, error, mutate } = useSWR(
    `${process.env.REACT_APP_BASE_URL}surveyruns/${surveyID}/?surveyDetails`,
    auth_fetcher,
    { refreshInterval: 5 * 1000 } // Revalidate every 5 seconds
  );

  const [edit, setEdit] = useState(false);

  if (error) return <div>failed to load</div>;
  if (!data) return <FullScreenLoader />;

  if (data.done) navigate(`/dashboard/auswertung/${surveyID}`);
  else {
    const surveys = {
      teacher: data["individual_surveys"].filter((el) => el.type === "L"),
      expert: data["individual_surveys"].filter((el) => el.type === "E"),
      student: data["individual_surveys"].filter((el) => el.type === "S"),
    };

    return (
      <>
        <CustomHelmet pageTitle={`Umfrage in ${data.subject}`} />
        <Breadcrumbs
          listItems={[
            { name: "Home", url: "/" },
            { name: "Umfragen", url: "/dashboard" },
            {
              name: `Zugangscodes Umfrage (${data.subject}, ${DateTime.fromISO(
                data.date
              ).toLocaleString(DateTime.DATE_SHORT, {
                locale: "de-CH",
              })})`,
              url: `/dashboard/teilnahmeliste/${surveyID}`,
            },
          ]}
        />
        <BaseContainer className={customStyles.tanList}>
          <Intro
            title={`Umfrage im Fach ${data.subject} vom ${DateTime.fromISO(
              data.date
            ).toLocaleString(DateTime.DATE_MED, { locale: "de-CH" })}`}
          >
            <p>
              Hier finden Sie automatisch generierte Zugangscodes, um den
              verschiedenen Perspektiven eine anonymisierte Teilnahme an der
              Umfrage zu gewährleisten.
            </p>
            <p>
              Sie können die Zugangscodes ausdrucken und den entsprechenden
              Personen aushändigen. Sobald ein Zugangscode von einer Person zum
              Ausfüllen der Umfrage verwendet worden ist, erscheint der
              entsprechende Code in dieser Übersicht als durchgestrichen.
            </p>
          </Intro>
          <div>
            {/* TODO: Better print button */}
            <PrintQRCodes surveys={surveys} />
            <button
              className="Button color-border-white size-large"
              style={{ marginLeft: "1rem" }}
              onClick={() => setEdit((p) => !p)}
            >
              {edit ? "Beabeitung beenden" : "Zugangscodes bearbeiten"}
            </button>
            <div className="DatabaseQuery--table">
              <table className="ornate">
                <thead>
                  <tr>
                    <th>Zielgruppe</th>
                    <th>Zugangscodes</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>Lehrpersonen ({surveys.teacher.length})</td>
                    <td className="TagList">
                      <ul className="TagList--list">
                        {surveys.teacher.map((el) => (
                          <li key={el.tan}>
                            <div
                              className="TagList--tag"
                              style={{
                                textDecoration: el.done
                                  ? "line-through"
                                  : "none",
                              }}
                            >
                              {!edit ? (
                                <a
                                  href={`/umfrage/${el.tan}`}
                                  className="whitespace-nowrap !text-black"
                                >
                                  {el.tan}
                                </a>
                              ) : (
                                el.tan
                              )}{" "}
                              {!el.done && edit && (
                                <button
                                  onClick={async () => {
                                    await axios.delete(el.url);
                                    await mutate();
                                  }}
                                >
                                  ✕
                                </button>
                              )}
                            </div>
                          </li>
                        ))}
                        {edit && (
                          <li>
                            <button
                              className="TagList--tag"
                              onClick={async () => {
                                await axios.post(
                                  `${process.env.REACT_APP_BASE_URL}survey/`,
                                  {
                                    type: "L",
                                    survey_run: data["url"],
                                  },
                                  {
                                    headers: {
                                      Authorization: `Token ${getToken()}`,
                                    },
                                  }
                                );
                                await mutate();
                              }}
                            >
                              +
                            </button>
                          </li>
                        )}
                      </ul>
                    </td>
                  </tr>
                  <tr>
                    <td className="whitespace-nowrap">
                      Beobachter*innen ({surveys.expert.length})
                    </td>
                    <td className="TagList">
                      <ul className="TagList--list">
                        {surveys.expert.map((el) => (
                          <li key={el.tan}>
                            <div
                              className="TagList--tag"
                              style={{
                                textDecoration: el.done
                                  ? "line-through"
                                  : "none",
                              }}
                            >
                              {!edit ? (
                                <a
                                  href={`/umfrage/${el.tan}`}
                                  className="whitespace-nowrap !text-black"
                                >
                                  {el.tan}
                                </a>
                              ) : (
                                el.tan
                              )}{" "}
                              {!el.done && edit && (
                                <button
                                  onClick={async () => {
                                    await axios.delete(el.url);
                                    await mutate();
                                  }}
                                >
                                  ✕
                                </button>
                              )}
                            </div>
                          </li>
                        ))}
                        {edit && (
                          <li>
                            <button
                              className="TagList--tag"
                              onClick={async () => {
                                await axios.post(
                                  `${process.env.REACT_APP_BASE_URL}survey/`,
                                  {
                                    type: "E",
                                    survey_run: data["url"],
                                  },
                                  {
                                    headers: {
                                      Authorization: `Token ${getToken()}`,
                                    },
                                  }
                                );
                                await mutate();
                              }}
                            >
                              +
                            </button>
                          </li>
                        )}
                      </ul>
                    </td>
                  </tr>
                  <tr>
                    <td className="whitespace-nowrap">
                      Schüler*innen ({surveys.student.length})
                    </td>
                    <td className="TagList">
                      <ul className="TagList--list">
                        {surveys.student.map((el) => (
                          <li key={el.tan}>
                            <div
                              className="TagList--tag"
                              style={{
                                textDecoration: el.done
                                  ? "line-through"
                                  : "none",
                              }}
                            >
                              {!edit ? (
                                <a
                                  href={`/umfrage/${el.tan}`}
                                  className="whitespace-nowrap !text-black"
                                >
                                  {el.tan}
                                </a>
                              ) : (
                                el.tan
                              )}{" "}
                              {!el.done && edit && (
                                <button
                                  onClick={async () => {
                                    await axios.delete(el.url);
                                    await mutate();
                                  }}
                                >
                                  ✕
                                </button>
                              )}
                            </div>
                          </li>
                        ))}
                        {edit && (
                          <li>
                            <button
                              className="TagList--tag"
                              onClick={async () => {
                                await axios.post(
                                  `${process.env.REACT_APP_BASE_URL}survey/`,
                                  {
                                    type: "S",
                                    survey_run: data["url"],
                                  },
                                  {
                                    headers: {
                                      Authorization: `Token ${getToken()}`,
                                    },
                                  }
                                );
                                await mutate();
                              }}
                            >
                              +
                            </button>
                          </li>
                        )}
                      </ul>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </BaseContainer>
      </>
    );
  }
}
